import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  CountryIso2,
  defaultCountries,
  parseCountry,
  usePhoneInput,
} from 'react-international-phone';
import { TextFieldProps as MuiTextFieldProps } from '@mui/material/TextField';
import {
  StyledFlagImage,
  StyledInputAdornment,
  StyledMenuItem,
  StyledSelect,
  StyledSelectMenuProps,
  StyledTextInput,
} from './styles';
import Typography from '../Typography';
import { UsePhoneInputConfig } from 'react-international-phone/dist/hooks/usePhoneInput';

export interface PhoneInputProps
  extends Omit<MuiTextFieldProps, 'variant' | 'value' | 'onChange' | 'type'>,
    Pick<UsePhoneInputConfig, 'disableDialCodePrefill'> {
  completed?: boolean;
  defaultCountry?: CountryIso2;
  onChange?: (phone: string) => void;
  value?: unknown;
}

export default function PhoneInput(props: PhoneInputProps) {
  const {
    value,
    onChange,
    disabled,
    InputProps,
    disableDialCodePrefill,
    completed = false,
    defaultCountry = 'us',
    ...phoneInputProps
  } = props;

  const theme = useTheme();
  const phoneValue =
    typeof value === 'number' || typeof value === 'string' ? String(value) : '';
  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } =
    usePhoneInput({
      value: phoneValue,
      defaultCountry,
      disableDialCodePrefill,
      countries: defaultCountries,
      onChange: (data) => {
        if (onChange) {
          onChange(data.phone);
        }
      },
    });

  return (
    <StyledTextInput
      value={inputValue}
      onChange={handlePhoneValueChange}
      type="tel"
      inputRef={inputRef}
      disabled={disabled}
      InputProps={{
        startAdornment: (
          <StyledInputAdornment position="start">
            <StyledSelect
              IconComponent={ExpandMoreIcon}
              MenuProps={StyledSelectMenuProps}
              disabled={disabled}
              value={country.iso2}
              onChange={(e) => setCountry(e.target.value as string)}
              renderValue={(renderedValue) => (
                <StyledFlagImage iso2={renderedValue as string} />
              )}
            >
              {defaultCountries.map((countryData) => {
                const parsedCountry = parseCountry(countryData);

                return (
                  <StyledMenuItem
                    key={parsedCountry.iso2}
                    value={parsedCountry.iso2}
                  >
                    <StyledFlagImage iso2={parsedCountry.iso2} />
                    <Typography color={theme.palette.neutral[800]}>
                      {parsedCountry.name}
                    </Typography>
                    <Typography color={theme.palette.neutral[600]}>
                      +{parsedCountry.dialCode}
                    </Typography>
                  </StyledMenuItem>
                );
              })}
            </StyledSelect>
          </StyledInputAdornment>
        ),
        ...InputProps,
      }}
      completed={completed}
      {...phoneInputProps}
    />
  );
}
